/**
 * Product Page JavaScript
 * Handles carousel, variants, quantity, wishlist, and add to cart
 */

class ProductPage {
  constructor() {
    this.product = null;
    this.currentVariant = null;
    this.init();
  }

  init() {
    // Initialize carousel
    this.initCarousel();

    // Initialize variant selectors
    this.initVariantSelectors();

    // Initialize quantity controls
    this.initQuantityControls();

    // Initialize wishlist
    this.initWishlist();

    // Initialize add to cart
    this.initAddToCart();
  }

  /**
   * Image Carousel
   */
  initCarousel() {
    const carousel = document.getElementById('product-carousel-' + this.getSectionId());
    if (!carousel) return;

    this.currentSlideIndex = 0;
    this.slides = carousel.querySelectorAll('.carousel-slide');
    this.thumbnails = carousel.querySelectorAll('.thumbnail');
    this.prevButton = carousel.querySelector('.carousel-prev');
    this.nextButton = carousel.querySelector('.carousel-next');

    if (this.slides.length <= 1) return;

    // Previous button
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prevSlide());
    }

    // Next button
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }

    // Thumbnail clicks
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => this.goToSlide(index));
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prevSlide();
      if (e.key === 'ArrowRight') this.nextSlide();
    });

    // Touch/swipe support
    this.initSwipe(carousel);
  }

  prevSlide() {
    this.currentSlideIndex = (this.currentSlideIndex - 1 + this.slides.length) % this.slides.length;
    this.updateSlide();
  }

  nextSlide() {
    this.currentSlideIndex = (this.currentSlideIndex + 1) % this.slides.length;
    this.updateSlide();
  }

  goToSlide(index) {
    this.currentSlideIndex = index;
    this.updateSlide();
  }

  updateSlide() {
    // Update slides
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('active', index === this.currentSlideIndex);
    });

    // Update thumbnails
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('active', index === this.currentSlideIndex);
    });
  }

  initSwipe(element) {
    let startX = 0;
    let endX = 0;

    element.addEventListener('touchstart', (e) => {
      startX = e.changedTouches[0].screenX;
    }, { passive: true });

    element.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].screenX;
      this.handleSwipe(startX, endX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
  }

  /**
   * Variant Selectors
   */
  initVariantSelectors() {
    const variantSelects = document.querySelectorAll('.variant-select');
    if (!variantSelects.length) return;

    variantSelects.forEach(select => {
      select.addEventListener('change', () => this.handleVariantChange());
    });

    // Get product JSON from Shopify
    this.loadProductData();
  }

  async loadProductData() {
    const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    if (!productHandle) return;

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      this.product = await response.json();
      this.currentVariant = this.product.variants.find(v => v.available) || this.product.variants[0];
      this.updatePrice();
    } catch (error) {
      console.error('Error loading product data:', error);
    }
  }

  handleVariantChange() {
    if (!this.product) return;

    // Get all selected dropdown values
    const selects = document.querySelectorAll('.variant-select');
    const selectedOptions = Array.from(selects).map(select => select.value);

    // Find matching variant
    const variant = this.product.variants.find(v => {
      return v.options.every((option, index) => option === selectedOptions[index]);
    });

    if (variant) {
      this.currentVariant = variant;
      this.updatePrice();
      this.updateAvailability();
      this.updateURL(variant.id);

      // Update hidden variant ID input
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // Update media if variant has image
      if (variant.featured_image) {
        this.updateMediaForVariant(variant);
      }
    }
  }

  updatePrice() {
    if (!this.currentVariant) return;

    const priceContainer = document.querySelector('[data-price-container]');
    if (!priceContainer) return;

    const price = this.currentVariant.price;
    const compareAtPrice = this.currentVariant.compare_at_price;

    const priceElement = priceContainer.querySelector('.price-amount');
    if (priceElement) {
      const dollars = Math.floor(price / 100);
      priceElement.innerHTML = `<span class="currency">$</span>${dollars}`;
    }

    // Update compare at price
    const compareElement = priceContainer.querySelector('.price-compare');
    if (compareAtPrice && compareAtPrice > price) {
      if (compareElement) {
        compareElement.textContent = `$${Math.floor(compareAtPrice / 100)}`;
        compareElement.parentElement.style.display = 'flex';
      }
    } else if (compareElement) {
      compareElement.parentElement.style.display = 'none';
    }
  }

  updateAvailability() {
    const addButton = document.querySelector('.product-add-to-cart-button');
    if (!addButton) return;

    if (this.currentVariant && this.currentVariant.available) {
      addButton.disabled = false;
      addButton.textContent = addButton.dataset.addText || 'Add to cart';
    } else {
      addButton.disabled = true;
      addButton.textContent = 'Sold Out';
    }
  }

  updateURL(variantId) {
    if (!variantId) return;
    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  updateMediaForVariant(variant) {
    const imageId = variant.featured_image?.id;
    if (!imageId) return;

    const targetSlide = document.querySelector(`.carousel-slide[data-media-id="${imageId}"]`);
    if (targetSlide) {
      const index = Array.from(this.slides).indexOf(targetSlide);
      if (index !== -1) {
        this.goToSlide(index);
      }
    }
  }

  /**
   * Quantity Controls
   */
  initQuantityControls() {
    const minusButtons = document.querySelectorAll('.quantity-minus');
    const plusButtons = document.querySelectorAll('.quantity-plus');
    const quantityInputs = document.querySelectorAll('.quantity-input');

    minusButtons.forEach(button => {
      button.addEventListener('click', () => {
        const input = button.parentElement.querySelector('.quantity-input');
        const currentValue = parseInt(input.value) || 1;
        if (currentValue > 1) {
          input.value = currentValue - 1;
        }
      });
    });

    plusButtons.forEach(button => {
      button.addEventListener('click', () => {
        const input = button.parentElement.querySelector('.quantity-input');
        const currentValue = parseInt(input.value) || 1;
        input.value = currentValue + 1;
      });
    });

    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const value = parseInt(e.target.value) || 1;
        e.target.value = Math.max(1, value);
      });
    });
  }

  /**
   * Wishlist Functionality
   */
  initWishlist() {
    const wishlistButton = document.querySelector('.wishlist-button');
    if (!wishlistButton) return;

    const productHandle = wishlistButton.dataset.productHandle;
    const productId = wishlistButton.dataset.productId;

    // Check if product is already in wishlist
    const wishlist = this.getWishlist();
    if (wishlist.includes(productId)) {
      wishlistButton.classList.add('active');
    }

    wishlistButton.addEventListener('click', () => {
      this.toggleWishlist(productId, wishlistButton);
    });
  }

  toggleWishlist(productId, button) {
    let wishlist = this.getWishlist();

    if (wishlist.includes(productId)) {
      // Remove from wishlist
      wishlist = wishlist.filter(id => id !== productId);
      button.classList.remove('active');
      this.showNotification('Removed from wishlist');
    } else {
      // Add to wishlist
      wishlist.push(productId);
      button.classList.add('active');
      this.showNotification('Added to wishlist');
    }

    // Save to localStorage
    localStorage.setItem('olgvml_wishlist', JSON.stringify(wishlist));

    // Optionally sync with customer metafields if logged in
    if (window.Shopify && window.Shopify.customer) {
      this.syncWishlistToCustomer(wishlist);
    }
  }

  getWishlist() {
    try {
      const wishlist = localStorage.getItem('olgvml_wishlist');
      return wishlist ? JSON.parse(wishlist) : [];
    } catch (error) {
      return [];
    }
  }

  async syncWishlistToCustomer(wishlist) {
    // This would require a server-side endpoint to update customer metafields
    // For now, we'll just store in localStorage
    console.log('Wishlist synced to localStorage:', wishlist);
  }

  /**
   * Add to Cart
   */
  initAddToCart() {
    const form = document.querySelector('.product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.addToCart(form);
    });
  }

  async addToCart(form) {
    const formData = new FormData(form);
    const addButton = form.querySelector('.product-add-to-cart-button');
    const errorContainer = form.querySelector('.product-form-error');

    // Disable button and show loading state
    const originalText = addButton.textContent;
    addButton.disabled = true;
    addButton.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: formData.get('id'),
          quantity: formData.get('quantity') || 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || errorData.message || 'Could not add to cart');
      }

      const data = await response.json();

      // Success
      this.showNotification('Added to cart!');

      // Animate cart icon
      this.animateCartIcon();

      // Update cart count bubble
      this.updateCartCount();

      // Hide error if showing
      if (errorContainer) {
        errorContainer.hidden = true;
      }
    } catch (error) {
      console.error('Add to cart error:', error);

      if (errorContainer) {
        errorContainer.querySelector('.error-message').textContent = error.message;
        errorContainer.hidden = false;
      } else {
        this.showNotification(error.message, 'error');
      }
    } finally {
      // Re-enable button
      addButton.disabled = false;
      addButton.textContent = originalText;
    }
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  async updateCartCount() {
    const cart = await this.getCart();
    if (!cart) return;

    const countBubbles = document.querySelectorAll('.cart-count-bubble');
    countBubbles.forEach(bubble => {
      const count = cart.item_count;
      bubble.textContent = count;
      bubble.classList.toggle('hidden', count === 0);
    });
  }

  /**
   * Animate Cart Icon
   */
  animateCartIcon() {
    const cartIcon = document.querySelector('.cart-icon');
    if (!cartIcon) return;

    // Add animation class
    cartIcon.classList.add('cart-icon-bounce');

    // Remove animation class after animation completes
    setTimeout(() => {
      cartIcon.classList.remove('cart-icon-bounce');
    }, 600);
  }

  /**
   * Notifications
   */
  showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `product-notification product-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 24px;
      right: 24px;
      padding: 16px 24px;
      background: ${type === 'success' ? '#008000' : '#ff4444'};
      color: #ffffff;
      border-radius: 8px;
      font-family: 'Roboto', sans-serif;
      font-size: 16px;
      font-weight: 600;
      z-index: 10000;
      box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.2);
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Helper: Get section ID
   */
  getSectionId() {
    const section = document.querySelector('[id^="product-carousel-"]');
    return section ? section.id.replace('product-carousel-', '') : '';
  }
}

// Add notification animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductPage();
  });
} else {
  new ProductPage();
}
